/**
 * Grid Layout Strategy
 *
 * Grid mode displays two dimensions as a 2D grid (rows and columns),
 * with the third dimension controlled via a slider in the header/footer.
 *
 * This file contains both the data transformation logic and control
 * placement logic for grid layouts.
 */

import * as _ from 'lodash';

import {type MediaBrowserPanelConfig} from '../common';
import {
  getAggregatedAvailableIndices,
  getAggregatedAvailableSteps,
} from '../utils/stepUtils';
import {type LayoutStrategy} from './LayoutStrategy';
import {
  type ControlData,
  type GridData,
  type RowData,
  type SlotRegistry,
  type TransformInput,
} from './types';
import {getAxisValuesWithOptions} from './utils/axisValues';
import {buildGridRows, getControlledDimension} from './utils/gridRows';
import {
  caption,
  compareEntry,
  indexSlider,
  label,
  stepSlider,
  xAxisRangeSlider,
} from './utils/slots';
import {createEmptySlotRegistry} from './utils/slotUtils';

// Debug logging flag - set to false to disable verbose console logs
const DEBUG = false;
const log = DEBUG ? console.log : () => {};

export const GridLayout: LayoutStrategy = {
  id: 'grid',

  transform(input: TransformInput): GridData {
    const {runs, config} = input;
    const {gridSettings, mediaKeys, maxYAxisCount = 100} = config;
    const xAxis = gridSettings?.xAxis ?? 'step';
    const yAxis = gridSettings?.yAxis ?? 'run';
    const snapToExistingStep = config.snapToExistingStep ?? true;

    if (!mediaKeys || mediaKeys.length === 0) {
      return {rows: [], metadata: {layoutType: 'grid', totalCells: 0}};
    }

    if (runs.length === 0) {
      return {rows: [], metadata: {layoutType: 'grid', totalCells: 0}};
    }

    log('[GridLayout] Starting transform:', {
      xAxis,
      yAxis,
      snapToExistingStep,
      runCount: runs.length,
      maxRows: maxYAxisCount,
    });

    // Use column-by-column logic for all grid modes
    // The old makeLayout uses a single nested loop structure that works for all cases
    return buildColumnByColumnLogic(input, xAxis, yAxis, maxYAxisCount);
  },

  buildSlotRegistry(
    config: MediaBrowserPanelConfig,
    stepMeta?: TransformInput['stepMeta'],
    _isFullScreen: boolean = false,
    isSingleRunView?: boolean
  ): SlotRegistry {
    const xAxis = config.gridSettings?.xAxis ?? 'step';
    const yAxis = config.gridSettings?.yAxis ?? 'run';
    const columnCount = config.columnCount ?? 3;

    // Build slot registry organized by LOCATION
    const slots = createEmptySlotRegistry();

    // Add controls based on which dimensions are on axes vs controlled

    // Step control
    if (xAxis === 'step') {
      // Step is on x-axis (column headers)
      slots.tileHeader!.push(label('step'));
    } else if (yAxis === 'step') {
      // Step is on y-axis (row headers)
      slots.rowHeader!.push(label('step'));
    } else {
      // Step is controlled (slider in footer)
      slots.contentFooter!.push(stepSlider());
    }

    // Index control
    if (xAxis === 'index') {
      // Index is on x-axis (column headers)
      slots.tileHeader!.push(label('index'));
    } else if (yAxis === 'index') {
      // Index is on y-axis (row headers)
      // matching prod with this, but it seems inconsistent with the other axes
      slots.rowHeader!.push(label('index'));
    } else if (stepMeta?.indices && stepMeta?.indices?.length > 1) {
      // Index is controlled (slider in footer)
      slots.contentFooter!.push(indexSlider());
    }

    // Run control
    if (xAxis === 'run') {
      // Run is on x-axis (show on each tile)
      slots.tileHeader!.push(label('run', 'value'));
    } else if (yAxis === 'run') {
      // Run is on y-axis (row headers)
      slots.rowHeader!.push(label('run', 'value'));
    }

    // Add x-axis range slider when xAxis is step and we have multiple columns
    // (allows zooming into a subset of the horizontal axis)
    if (xAxis === 'step' && columnCount > 1) {
      slots.contentFooter!.push(xAxisRangeSlider());
    }

    // Add captions to tile-footer
    slots.tileFooter = [caption()];

    // Note: We don't add y-axis range slider because when step is on y-axis,
    // we're showing all steps as rows (no need to filter/zoom vertically)

    return slots;
  },

  buildRowData(input: TransformInput, gridData: GridData): RowData[] {
    const {config, runs} = input;
    const yAxis = config.gridSettings?.yAxis ?? 'run';
    const yAxisTicks = gridData.metadata.yAxisTicks ?? [];

    return Array.from({length: gridData.rows.length}, (_, i) => {
      const tick = yAxisTicks[i];

      if (yAxis === 'step') {
        return {step: tick};
      } else if (yAxis === 'index') {
        return {index: tick};
      } else {
        // yAxis === 'run'
        return {run: tick != null ? runs[tick]?.name : undefined};
      }
    });
  },

  buildControlData(input: TransformInput, gridData: GridData): ControlData[] {
    const {config, stepMeta, runs, currentStep, currentIndex} = input;
    const columnCount = config.columnCount ?? 1;
    const mediaKeys = config.mediaKeys ?? [];
    const groupSize = mediaKeys.length > 0 ? mediaKeys.length : 1;
    const xAxisStrategy = config.gridSettings?.xAxis ?? 'step';
    const xAxisTicks = gridData.metadata.xAxisTicks ?? [];

    const availableSteps = getAggregatedAvailableSteps(stepMeta);
    const availableIndices = getAggregatedAvailableIndices(stepMeta);

    return Array.from({length: columnCount}, (_, i) => {
      const groupIndex = Math.floor(i / groupSize);
      const keyIndex = i % groupSize;
      const mediaKey = mediaKeys[keyIndex];

      let step: number;
      let index: number;
      let run: string | undefined;

      if (xAxisStrategy === 'step') {
        step = xAxisTicks[groupIndex] ?? config.stepIndex ?? currentStep;
        index = config.mediaIndex ?? currentIndex;
        run = runs[0]?.name;
      } else if (xAxisStrategy === 'index') {
        step = config.stepIndex ?? currentStep;
        index = xAxisTicks[groupIndex] ?? config.mediaIndex ?? currentIndex;
        run = runs[0]?.name;
      } else {
        // xAxis === 'run'
        step = config.stepIndex ?? currentStep;
        index = config.mediaIndex ?? currentIndex;
        const runIdx = xAxisTicks[groupIndex];
        run = runIdx != null ? runs[runIdx]?.name : undefined;
      }

      return {step, index, run, mediaKey, availableSteps, availableIndices};
    });
  },
};

// Helper functions (internal to this layout)

/**
 * Builds grid using column-by-column logic.
 * This is the main grid building function used for all grid configurations.
 */
function buildColumnByColumnLogic(
  input: TransformInput,
  xAxis: string,
  yAxis: string,
  maxRows: number
): GridData {
  const {runs, stepMeta, config} = input;
  const {mediaKeys, columnCount, selection} = config;

  if (!mediaKeys || mediaKeys.length === 0) {
    return {rows: [], metadata: {layoutType: 'grid', totalCells: 0}};
  }

  const controlledDimension = getControlledDimension(xAxis, yAxis);
  const groupSize = mediaKeys.length;

  // Calculate effective column limit (columnCount divided by group size)
  const effectiveColumnCount = columnCount
    ? Math.floor(columnCount / groupSize)
    : undefined;

  // Extract selection ranges
  const minX = _.get(selection?.xAxis, 0);
  const maxX = _.get(selection?.xAxis, 1);
  const minY = _.get(selection?.yAxis, 0);
  const maxY = _.get(selection?.yAxis, 1);

  log('[buildColumnByColumnLogic] Starting with config:', {
    xAxis,
    yAxis,
    columnCount,
    effectiveColumnCount,
    maxRows,
    selection: {minX, maxX, minY, maxY},
  });

  // Get X-axis values (with filtering and sampling)
  const xAxisResult = getAxisValuesWithOptions(
    xAxis as any,
    runs,
    stepMeta,
    minX,
    maxX,
    effectiveColumnCount
  );

  // Get Y-axis values (with filtering and sampling)
  const yAxisResult = getAxisValuesWithOptions(
    yAxis as any,
    runs,
    stepMeta,
    minY,
    maxY,
    maxRows
  );
  // Build rows using helper function
  const rows = buildGridRows(
    xAxis,
    yAxis,
    xAxisResult.values,
    yAxisResult.values,
    controlledDimension,
    input,
    effectiveColumnCount
  );

  // Calculate metadata
  const totalCells = rows.reduce((sum, row) => sum + row.cells.length, 0);

  let historySampleRate: number | undefined;
  if (xAxis === 'step' && xAxisResult.values.length > 1) {
    const xValues = xAxisResult.values as number[];
    historySampleRate =
      (xValues[xValues.length - 1] - xValues[0] - 2) / xValues.length;
  } else if (yAxis === 'step' && yAxisResult.values.length > 1) {
    const yValues = yAxisResult.values as number[];
    historySampleRate =
      (yValues[yValues.length - 1] - yValues[0] - 2) / yValues.length;
  }

  log('[buildColumnByColumnLogic] Complete:', {
    rowCount: rows.length,
    totalCells,
    columnsPerRow: rows[0]?.cells.length,
    historySampleRate,
  });

  return {
    rows,
    metadata: {
      layoutType: 'grid',
      totalCells,
      axisData: {
        x: xAxisResult.values,
        y: yAxisResult.allValues,
      },
      xAxisTicks: xAxisResult.values,
      allXAxisValues: xAxisResult.allValues,
      yAxisTicks: yAxisResult.values,
      allYAxisValues: yAxisResult.allValues,
      historySampleRate,
    },
  };
}
